import React, { useState, useMemo, useEffect } from 'react';
import { Stepper } from '@shared/components/Stepper';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { CustomerInformationStep } from './stepComponents/CustomerVerification';
import { TransferDetailsStep } from './stepComponents/TransferDetailsStep';
import { DocumentUploadStep } from './stepComponents/DocumentUploadStep';
import { ReviewSubmitStep } from './stepComponents/ReviewSubmitStep';
import { FileMetadata } from '@features/cx/shared/types/consumerRequest';
import { useFormCompletionTracker } from '@shared/hooks/useFormCompletionTracker';
import { useNavigate } from 'react-router-dom';


export const CreateTransferRequest: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const storageKey = `transfer-request-progress-${remoteUtilityId}`;
  // File state management at parent level
  const [fileObjects, setFileObjects] = useState<File[]>([]);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
  const [documentsFormData, setDocumentsFormData] = useState<any>({});
  const { startTracking, logTimeUntilStep, stopTracking } = useFormCompletionTracker("Transfer");
  const navigate = useNavigate();

   useEffect(() => {
    const handlePopState = () => {
      if (!window.location.hash) {
        // when hash is cleared, redirect to parent route
        navigate("/cx/transfer", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);


  useEffect(() => {
      startTracking()
    }, [startTracking])
  // Handle file data changes from EvidenceAttachmentsStep
  const handleFileDataChange = (data: any) => {
    console.log("🚀 Parent received file data:", data);
    console.log("🚀 File objects:", data.file);
    console.log("🚀 File metadata:", data.fileMetadata);
    if (data.file) {
      console.log("setting fileObjects", data.file);

      setFileObjects(data.file);
    }

    setFileObjects(data.file || []);
    setFileMetadata(data.fileMetadata || []);
    setDocumentsFormData({
      notes: data.notes || "",
      incidentDate: data.incidentDate || "",
      expectedResolution: data.expectedResolution || "",
    });
  };

  // Store the handler on window for child components to access
  React.useEffect(() => {
    (window as any).handleFileDataChange = (data: any) => {
      console.log("🚀 Parent received file data:", data);
      if (data.file?.length > 0) {
        setFileObjects(data.file);
      }
      setFileObjects(data.file || []);
      setFileMetadata(data.fileMetadata || []);
      setDocumentsFormData({
        notes: data.notes || "",
        incidentDate: data.incidentDate || "",
        expectedResolution: data.expectedResolution || "",
      });
    };

    // Store file data on window for ReviewSubmitStep to access
    (window as any).fileData = {
      fileObjects,
      fileMetadata,
      documentsFormData,
    };

    return () => {
      delete (window as any).handleFileDataChange;
      delete (window as any).fileData;
    };
  }, [fileObjects, fileMetadata, documentsFormData]);

  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Customer Verification",
        slug: "customer-info", // Add slug for routing
        component: (
          <CustomerInformationStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Customer Information Help",
        infoDescription:
          "Enter the customer's account details. You can use the 'Look Up' button to automatically populate fields if you have the account number.",
      },
      {
        title: "Transfer Details",
        slug: "transfer-details", // Add slug for routing
        component: (
          <TransferDetailsStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Transfer Details Help",
        infoDescription: "Enter the details of the transfer.",
      },
      {
        title: "Document Upload",
        slug: "document-upload", // Add slug for routing
        component: (
          <DocumentUploadStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            onDataChange={handleFileDataChange}
            logTimeUntilStep={logTimeUntilStep}
          />
        ),
        optional: false,
        infoTitle: "Document Upload Help",
        infoDescription: "Upload the required documents for the transfer.",
      },
      {
        title: "Review & Submit",
        slug: "review-submit", // Add slug for routing
        component: (
          <ReviewSubmitStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            fileObjects={fileObjects}
            fileMetadata={fileMetadata}
            documentsFormData={documentsFormData}
            logTimeUntilStep={logTimeUntilStep}
            stopTracking={stopTracking}
          />
        ),
        optional: false,
        infoTitle: "Review & Submit Help",
        infoDescription:
          "Review all your transfer details and submit the request.",
      },
    ],
    []
  ); // Only recreate when requestData or handleStateUpdate changes

  return (
    <div className="mx-auto p-6">
      <div className="mb-6">
        <h1 className="page-title">
          Create Transfer Request
        </h1>
        <p className="page-subtitle">
          Complete the following steps to submit a transfer request.
        </p>
      </div>

      <Stepper
        steps={steps}
        onStepChange={() => {}}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // Storage is handled at component level using step helpers
        }}
        enableApiSave={false}
        saveOnStepChange={false}
        trackDirtyState={false}
        autoSave={true}
        persistProgress={true}
        allowSkip={false} // Set to false since all steps are required
        storageKey={storageKey}
        enableRouting={true} // Enable hash-based routing (no 404s on refresh)
        onValidation={undefined} // basePath is not needed for hash-based routing
        onComplete={() => {}}
        showDraftResume={true}
      />
    </div>
  );
};
